import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ProjectDetail from '../components/projects/ProjectDetail';
import ProjectAuditDrawer from '../components/projects/ProjectAuditDrawer';
import { useProject } from '../hooks/useProjects';
import { useExpenses } from '../hooks/useExpenses';

const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [isAuditDrawerOpen, setIsAuditDrawerOpen] = useState(false);
  
  // Fetch project data
  const { data: project, isLoading: projectLoading, isError: projectError } = useProject(projectId || '');
  
  // Fetch all expenses and filter by project
  const { data: allExpenses, isLoading: expensesLoading } = useExpenses();
  
  const loading = projectLoading || expensesLoading;
  
  // Filter expenses for this project
  const expenses = allExpenses?.items?.filter(exp => exp.projectId === projectId) || [];

  if (!projectId) {
    navigate('/projects');
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Proyecto no encontrado
        </h2>
        <p className="text-gray-600 mb-4">
          El proyecto que buscas no existe o no tienes acceso a él.
        </p>
        <button
          onClick={() => navigate('/projects')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Volver a Proyectos
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Back Button and Action Buttons */}
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={() => navigate('/projects')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <svg
            className="w-5 h-5 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Volver a Proyectos
        </button>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/expenses?project=${projectId}`)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Agregar Gasto
          </button>
          <button
            onClick={() => navigate(`/invoices?project=${projectId}`)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Agregar Factura
          </button>
          <button
            onClick={() => setIsAuditDrawerOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Historial
          </button>
        </div>
      </div>

      {/* Project Detail */}
      <ProjectDetail project={project} expenses={expenses} />
      
      {/* Audit Drawer */}
      <ProjectAuditDrawer
        projectId={projectId}
        isOpen={isAuditDrawerOpen}
        onClose={() => setIsAuditDrawerOpen(false)}
      />
    </div>
  );
};

export default ProjectDetailPage;
